import { CommonModule } from '@angular/common';
import { Component, ElementRef, inject, ViewChild } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder } from '@angular/forms';

import { SharedModule } from '@app/shared/shared.module';
import { TranslateModule } from '@ngx-translate/core';

import { ChangeStepService } from '@app/core/components/stepper/service/change-step.service';
import { ChangeDeviceService } from '@app/shared/services/change-device.service';
import { SaveDataService } from '@app/shared/services/save-data.service';

import { CoreModule } from '@app/core/core.module';
import { AppService } from '@app/app.service';

@Component({
    selector: 'app-bank-details',
    standalone: true,
    imports: [CoreModule, CommonModule, SharedModule, TranslateModule],
    templateUrl: './bank-details.component.html',
    styleUrls: ['./bank-details.component.scss'],
})
export class BankDetailsComponent {
    @ViewChild('ibanClick') searchElement!: ElementRef;

    private appData = inject(AppService);

    continueButton = 'common.buttons.continueButton';
    backButton = 'common.buttons.backButton';

    bankAccountTitle = 'features.bankDetails.bankAccountTitle';
    bankAccountSubtitle1 = 'features.bankDetails.bankAccountSubtitle1';
    bankAccountSubtitle2 = 'features.bankDetails.bankAccountSubtitle2';

    iban = 'features.bankDetails.iban';
    infoIban = 'features.bankDetails.infoIban';

    backButtonDisabled = false;
    continueButtonDisabled = true;

    isPortraitDevice = false;

    formGroup!: FormGroup;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private changeDeviceService: ChangeDeviceService,
        private saveDataService: SaveDataService,
        private changeStepService: ChangeStepService,
        private fb: FormBuilder,
    ) {
        this.changeStepService.changeStep(21);
    }

    ngOnInit() {
        this.isPortraitDevice = this.changeDeviceService.isPortraitDevice;
        this.changeDeviceService.changedDevice.subscribe((value) => {
            this.isPortraitDevice = value;
        });

        this.createForm();
        this.setValuesForm(this.appData.data.userData);
        //this.searchElement.nativeElement.focus();
        this.formIsValid();
    }

    createForm() {
        this.formGroup = this.fb.group({
            iban: ['', [Validators.required, this.ibanValidator.bind(this)]],
        });
    }

    setValuesForm(value?: any) {
        if (value != undefined) {
            let ibanFormated = formatIBAN(value.bankDetails.iban ? value.bankDetails.iban : '');
            this.formGroup = this.fb.group({
                iban: [ibanFormated,
                    [Validators.required, this.ibanValidator.bind(this)]],
            });
        }
    }

    ibanValidator(control: any) {
        const iban = control.value;
        if (iban && !validarIBAN(iban)) {
            return { invalidIBAN: true };
        }
        return null;
    }

    //-------------------------//
    //------EVENT METHODS------//
    //-------------------------//

    onInputIban(event: Event) {
        const ibanControl = this.formGroup.get('iban');
        if (ibanControl) {
            let formattedIBAN = formatIBAN(ibanControl.value);
            // Si el IBAN no tiene el código de país, asumimos que es ES (España)
            if (formattedIBAN.length < 4) {
                formattedIBAN = 'ES' + formattedIBAN;
            }
            ibanControl.setValue(formattedIBAN, { emitEvent: false });
        }
        this.formIsValid();
    }

    onClickIban() {
        this.searchElement.nativeElement.focus();
        this.formIsValid();
    }

    //-------------------------//
    //-------VALIDATION--------//
    //-------------------------//

    formIsValid() {
        this.continueButtonDisabled = !this.formGroup.valid;
    }

    //-------------------------//
    //--------NAVIGATION-------//
    //-------------------------//

    onClicButtonBack() {
        this.saveDataService.saveDataLocal(this.formGroup.value, 'bank-details');
        //this.router.navigate(['/main/co-holder'], { relativeTo: this.route });
        this.router.navigate(['/main/preauthorized'], { relativeTo: this.route });
    }

    onClicButtonContinue() {
        this.saveDataService.saveDataLocal(this.formGroup.value, 'bank-details');
        this.router.navigate(['/main/step/recruitment'], { relativeTo: this.route });
    }

    ngOnDestroy() {}
}

export function validarIBAN(iban: string): boolean {
    // Eliminar espacios en blanco y convertir a mayúsculas
    iban = iban.replace(/\s+/g, '').toUpperCase();

    // Si el IBAN no tiene el código de país, asumimos que es ES (España)
    if (iban.length > 0 && iban.length < 4 && 'ES' != iban) {
        iban = 'ES' + iban;
    }

    // Mover los primeros cuatro caracteres al final del IBAN
    iban = iban.slice(4) + iban.slice(0, 4);

    // Reemplazar cada letra por su valor numérico (A = 10, B = 11, ..., Z = 35)
    const numericIban = iban.split('').map(char => {
        const code = char.charCodeAt(0);
        return code >= 65 ? code - 55 : char;
    }).join('');

    // Calcular el resto de la división del número resultante por 97
    const remainder = numericIban.split('').reduce((acc, digit) => {
        return (Number(acc) * 10 + Number(digit)) % 97;
    }, 0);

    return remainder === 1;
}

export function formatIBAN(iban: string): string {

    // Eliminar espacios en blanco y convertir a mayúsculas
    iban = iban.substring(0,2) + iban.substring(2).replace(/\s+/g, '').replace(/\D/g, '').toUpperCase();

    // Si el IBAN no tiene el código de país, asumimos que es ES (España)
    if (iban.length > 1 && 'ES' != iban.substring(0,2)) {
        iban = 'ES' + iban;
    }

    // Extraer el código de país y los dígitos de control
    const countryCode = iban.slice(0, 2);
    const checkDigits = iban.slice(2, 4);
    const accountNumber = iban.slice(4);

    // Formatear el número de cuenta en grupos de cuatro
    const formattedAccountNumber = accountNumber.match(/.{1,4}/g)?.join(' ') || '';

    return `${countryCode} ${checkDigits} ${formattedAccountNumber}`;

}
